import React from "react"

import {
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
} from "@mdi/js"
import Icon from "@mdi/react"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import MapOutlinedIcon from "@mui/icons-material/MapOutlined"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import {
	Avatar,
	Box,
	Chip,
	Divider,
	Grid,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material"
import {
	GoogleMap,
	InfoWindow,
	LoadScript,
	Marker,
} from "@react-google-maps/api"

import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import { t, Trans } from "@lingui/macro"
import { DeleteDocumentModal } from "components/asset/asset-details/asset-documents-table/delete-document-modal"
import UploadDocumentModal from "components/asset/asset-details/asset-documents-table/upload-document-modal"
import { LandlordCreateModal } from "components/asset/asset-details/asset-modals/landlord-create-modal"
import { LandlordModal } from "components/asset/asset-details/asset-modals/landlord-modal"
import { ManageFloorsModal } from "components/asset/asset-details/asset-modals/manage-floors-modal"
import {
	PageHeader,
	RoutesData,
} from "components/commons"
import SendNewProposal from "components/match-proposal/match-proposal-creation/send-proposal/send-new-proposal"
import { BrandOption, SendNewProposalRequest } from "components/match-proposal/model"
import TemplateSelectionModal from "components/commons/template-selection-modal"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import {
	AccountType,
	Asset,
	AssetMatchBrand,
	AssetMatchesBrandStatus,
	AssetStatus,
	AssetType,
	AssetWithImage,
	CreateCharacteristicsRequest,
	CreateLandlordRequest,
	CreateLandlordResponse,
	CreateSpecificWorkflow,
	Document,
	DocumentType,
	Floor,
	LandlordDetails,
	ListLandlordsProjection,
	NewStepRequest,
	OrderType,
	PaginatedResponse,
	PositionType,
	SearchFilter, SessionResponse,
	UpdateAssetRequest,
	WorkflowObjectType,
} from "models"
import { AccountServices } from "services/account.services"
import { AssetService } from "services/asset.service"
import { ActionsServices } from "services/actions.services"
import { LandlordService } from "services/landlord.service"
import { MatchService } from "services/match.service"
import withUseRouter, { UseRouterParams } from "services/router"
import { WorkflowService } from "services/workflow.service"
import {
	getAiAnalysisNumber,
	getAiAnalysisValue,
	normalizeAiAnalysis,
} from "utils/ai-analysis.utils"
import { memoizeOne } from "utils/memoize-one"
import { SbSnackbar } from "../../../components/commons/sb-snackbar"
import { NewStep } from "components/workflow/modals"
import { AssetBrandMatchingSection } from "./components/asset-brand-matching-section"
import { AssetDocumentsSection } from "./components/asset-documents-section"
import { AssetHeroSection, EditableAssetMetric, EditableMetricState } from "./components/asset-hero-section"

interface AssetDetailsProps extends UseRouterParams {
	sessionData: SessionResponse
}

interface AssetDetailsState extends MatesComponentState {
	asset: AssetWithImage | null

	immobiliareLink: string

	documentsFilter: SearchFilter
	photoFilter: SearchFilter
	documentsData: PaginatedResponse<Document> | null
	photoData: PaginatedResponse<Document> | null

	landlordList: PaginatedResponse<ListLandlordsProjection> | null,
	likes: string[]
	likeAsset: boolean
	matchSearch: string
	selectedMatchIds: string[]
	expandedMatchIds: string[]
	openSendProposal: boolean
	openPrecompiledProposal: boolean
	selectedProposalBrands: BrandOption[]
	openLandlordModal: boolean
	landlordDetails: LandlordDetails | null
	openLandlordCreationModal: boolean
	landlordCreationLoading: boolean
	loading: boolean
	selectedPoiIndex: number | null
	openUploadDocumentModal: boolean
	openDeleteDocumentModal: boolean
	selectedDocumentId: string | null
	openQuickActions: boolean
	openWorkflow: boolean
	openReminder: boolean
	openFloorsModal: boolean
	isSavingFloors: boolean
	snackbarData: {
		openNotification: boolean,
		severity: 'error' | 'info' | 'success' | 'warning',
		notificationMessage: string
	},
	editableMetrics: Record<EditableAssetMetric, EditableMetricState>,
	editableStatus: {
		value: AssetStatus | "",
		isEditing: boolean
	},
	editableAddress: {
		value: string
		isEditing: boolean
	},
	editablePrice: {
		value: string
		isEditing: boolean
	},
	editableAssetType: {
		value: AssetType | ""
		isEditing: boolean
	},
	editableDescription: {
		value: string
	},
	neighborhoodTab: NeighborhoodTab
}

interface PoiEntry {
	name?: string
	nome?: string
	address?: string
	indirizzo?: string
	lat?: number
	lng?: number
	place_id?: string
	brand_nome?: string
	brand?: string | null
	brand_riconosciuto?: boolean | string | null
	categoria?: string
	distanza_metri?: number
	rating?: number | null
	user_ratings_total?: number | null
}

interface AiAnalysisPricingViewModel {
	aiMinMqAnno: number | null
	aiMaxMqAnno: number | null
	aiPriceMin: number | null
	aiPriceMax: number | null
	averageMqPriceInArea: number | null
	aiMinPerMq: number | null
	aiMaxPerMq: number | null
}

interface AiAnalysisViewModel {
	analysis: Record<string, any> | null
	analysisBullets: string[]
	aiInsightDescription: string
	poiEntries: PoiEntry[]
	poiMarkers: Array<PoiEntry & { lat: number; lng: number }>
	pricing: AiAnalysisPricingViewModel
	averageIncome: number | null
	averageAge: number | null
	educationPercentage: number | null
	educationLabel: string | null
	overallScore: number | null
}

type NeighborhoodTab = "brand" | "poi"
const NEIGHBORHOOD_MAX_VISIBLE_ITEMS = 8
class AssetDetails extends React.Component<AssetDetailsProps, AssetDetailsState> {
	private readonly matesComponent: MatesComponent<AssetDetailsProps, AssetDetailsState> = new MatesComponent(this)
	private activeLoadRequestId = 0
	private readonly editableMetricKeys: EditableAssetMetric[] = ["gla", "height", "shopWindows", "bathrooms"]
	private readonly getFilteredMatches = memoizeOne((matches: AssetMatchBrand[], matchSearch: string) => {
		const normalizedSearch = matchSearch.trim().toLowerCase()
		return matches
			.filter((match) => {
				if (normalizedSearch.length === 0) {
					return true
				}
				const brandName = match.brand?.name?.toLowerCase() ?? ""
				const companyName = match.brand?.company?.name?.toLowerCase() ?? ""
				return brandName.includes(normalizedSearch) || companyName.includes(normalizedSearch)
			})
			.sort((a, b) => (b.compatibility ?? 0) - (a.compatibility ?? 0))
	})
	private createEditableMetricsState = (): Record<EditableAssetMetric, EditableMetricState> => {
		return this.editableMetricKeys.reduce((acc, key) => {
			acc[key] = {
				value: "",
				isEditing: false,
			}
			return acc
		}, {} as Record<EditableAssetMetric, EditableMetricState>)
	}

	private getMetricNumericValue = (asset: Asset | null, metric: EditableAssetMetric): number | null => {
		if (!asset || !asset.characteristics) {
			return null
		}

		const characteristics = asset.characteristics
		switch (metric) {
			case "gla":
				return characteristics.gla ?? null
			case "height":
				return characteristics.height ?? null
			case "shopWindows":
				return characteristics.shopWindows ?? null
			case "bathrooms":
				return characteristics.toilet !== undefined && characteristics.toilet !== null
					? (characteristics.toilet ? 1 : 0)
					: null
			default:
				return null
		}
	}

	private buildEditableMetricsFromAsset = (asset: Asset | null): Record<EditableAssetMetric, EditableMetricState> => {
		const draft = this.createEditableMetricsState()
		this.editableMetricKeys.forEach((metric) => {
			const metricValue = this.getMetricNumericValue(asset, metric)
			draft[metric] = {
				value: metricValue !== null && metricValue !== undefined ? `${metricValue}` : "",
				isEditing: false,
			}
		})
		return draft
	}

	private buildEditableAddressState = (asset: Asset | null): { value: string; isEditing: boolean } => {
		return {
			value: asset?.address ?? "",
			isEditing: false,
		}
	}

	private buildEditablePriceState = (asset: Asset | null): { value: string; isEditing: boolean } => {
		const priceValue = asset?.characteristics?.price
		return {
			value: priceValue !== null && priceValue !== undefined ? `${priceValue}` : "",
			isEditing: false,
		}
	}

	private buildEditableAssetTypeState = (asset: Asset | null): { value: AssetType | ""; isEditing: boolean } => {
		const typeValue = asset?.characteristics?.type ?? ""
		return {
			value: typeValue,
			isEditing: false,
		}
	}

	private buildEditableDescriptionState = (asset: Asset | null): { value: string } => {
		return {
			value: asset?.description ?? "",
		}
	}

	private buildEditableStatusState = (asset: Asset | null): { value: AssetStatus | ""; isEditing: boolean } => {
		const statusValue = asset?.characteristics?.status ?? ""
		return {
			value: statusValue,
			isEditing: false,
		}
	}

	private get assetId(): string {
		return this.props.params.assetId
	}

	private get userIsBrand(): boolean {
		return this.props.sessionData.accountType === AccountType.BRAND
	}

	constructor(props: AssetDetailsProps) {
		super(props)

		this.state = {
			...this.matesComponent.setupState(),
			asset: null,
			immobiliareLink: "",
			documentsFilter: new SearchFilter(),
			photoFilter: {
				pageSize: 100,
				pageId: 0,
				orderType: null,
				orderBy: null
			},
			documentsData: null,
			photoData: null,
			landlordList: null,
			likes: [],
			likeAsset: false,
			matchSearch: "",
			selectedMatchIds: [],
			expandedMatchIds: [],
			openSendProposal: false,
			openPrecompiledProposal: false,
			selectedProposalBrands: [],
			openLandlordModal: false,
			landlordDetails: null,
			openLandlordCreationModal: false,
			landlordCreationLoading: false,
			loading: true,
			selectedPoiIndex: null,
			openUploadDocumentModal: false,
			openDeleteDocumentModal: false,
			selectedDocumentId: null,
			openQuickActions: false,
			openWorkflow: false,
			openReminder: false,
			openFloorsModal: false,
			isSavingFloors: false,
			snackbarData: {
				openNotification: false,
				severity: "info",
				notificationMessage: ""
			},
			editableMetrics: this.createEditableMetricsState(),
			editableStatus: this.buildEditableStatusState(null),
			editableAddress: this.buildEditableAddressState(null),
			editablePrice: this.buildEditablePriceState(null),
			editableAssetType: this.buildEditableAssetTypeState(null),
			editableDescription: this.buildEditableDescriptionState(null),
			neighborhoodTab: "brand",
		}
	}

	componentDidMount() {
		this.loadAssetPage()
	}

	componentDidUpdate(prevProps: AssetDetailsProps) {
		if (prevProps.params.assetId !== this.props.params.assetId) {
			this.setState(
				() => ({
					asset: null,
					documentsData: null,
					photoData: null,
					likes: [],
					likeAsset: false,
					selectedPoiIndex: null,
					matchSearch: "",
					selectedMatchIds: [],
					expandedMatchIds: [],
					openSendProposal: false,
					openPrecompiledProposal: false,
					selectedProposalBrands: [],
					openLandlordModal: false,
					landlordDetails: null,
					openLandlordCreationModal: false,
					landlordCreationLoading: false,
					openFloorsModal: false,
					isSavingFloors: false,
					editableMetrics: this.createEditableMetricsState(),
					editableStatus: this.buildEditableStatusState(null),
					editableAddress: this.buildEditableAddressState(null),
					editablePrice: this.buildEditablePriceState(null),
					editableAssetType: this.buildEditableAssetTypeState(null),
					editableDescription: this.buildEditableDescriptionState(null),
					neighborhoodTab: "brand",
				}),
				this.loadAssetPage
			)
		}
	}

	componentWillUnmount() {
		this.activeLoadRequestId += 1
	}

	private isLoadRequestActive = (requestId: number): boolean => requestId === this.activeLoadRequestId

	private handleNeighborhoodTabChange = (_event: React.SyntheticEvent, value: NeighborhoodTab) => {
		this.setState(() => ({ neighborhoodTab: value }))
	}

	private loadAssetPage = (): void => {
		const requestId = ++this.activeLoadRequestId
		this.setState(() => ({ loading: true }))
		this.getLandlord(requestId)
	}

	private getAsset = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		AssetService.getAsset(
			this.assetId,
			(response: RestResponse<Asset, ErrorResponse>) => this.getAssetCallback(response, requestId),
			(response: RestResponse<Asset, ErrorResponse>) => this.getAssetCallbackError(response, requestId)
		)
	}

	private getAssetCallback = (response: RestResponse<Asset, ErrorResponse>, requestId: number): void => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		if (!response.data) {
			this.setState(() => ({
				asset: null,
				loading: false,
				editableMetrics: this.createEditableMetricsState(),
				editableStatus: this.buildEditableStatusState(null),
				editableAddress: this.buildEditableAddressState(null),
				editablePrice: this.buildEditablePriceState(null),
				editableAssetType: this.buildEditableAssetTypeState(null),
				editableDescription: this.buildEditableDescriptionState(null),
				neighborhoodTab: "brand",
			}))
			return
		}

		const assetWithImage: AssetWithImage = {
			asset: response.data,
			image: "",
		}

		let immobiliareLink: string = ""

		response.data.links.forEach((link) => {
			if (link.kind === "IMMOBILIARE_IT") {
				immobiliareLink = link.value
			}
		})

		this.setState(() => ({
			asset: assetWithImage,
			immobiliareLink: immobiliareLink,
			editableMetrics: this.buildEditableMetricsFromAsset(assetWithImage.asset),
			editableStatus: this.buildEditableStatusState(assetWithImage.asset),
			editableAddress: this.buildEditableAddressState(assetWithImage.asset),
			editablePrice: this.buildEditablePriceState(assetWithImage.asset),
			editableAssetType: this.buildEditableAssetTypeState(assetWithImage.asset),
			editableDescription: this.buildEditableDescriptionState(assetWithImage.asset),
			neighborhoodTab: "brand",
		}), () => {
			this.getDocuments(requestId)
			if (this.userIsBrand) {
				this.getAssetLikes(requestId)
			}
		})
	}

	private getAssetCallbackError = (response: RestResponse<Asset, ErrorResponse>, requestId: number): void => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.setState(() => ({
			asset: null,
			loading: false,
			editableMetrics: this.createEditableMetricsState(),
			editableStatus: this.buildEditableStatusState(null),
			editableAddress: this.buildEditableAddressState(null),
			editablePrice: this.buildEditablePriceState(null),
			editableAssetType: this.buildEditableAssetTypeState(null),
			editableDescription: this.buildEditableDescriptionState(null),
			neighborhoodTab: "brand",
		}))
		this.matesComponent.onError(response)
	}

	private getDocuments = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		AssetService.getDocuments(
			this.assetId,
			DocumentType.ATTACHMENT,
			this.state.documentsFilter,
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getDocumentsCallback(response, requestId),
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getDocumentsCallbackError(response, requestId),
			this.userIsBrand
		)
	}

	private getDocumentsCallback = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		const attachmentsData = response.data
		if (!attachmentsData) {
			this.setState(
				() => ({ documentsData: null }),
				() => this.getPhotos(requestId)
			)
			return
		}

		this.fetchAiReportDocuments(attachmentsData, requestId)
	}

	private fetchAiReportDocuments = (attachmentsData: PaginatedResponse<Document>, requestId: number) => {
		AssetService.getDocuments(
			this.assetId,
			DocumentType.AI_REPORT,
			this.state.documentsFilter,
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getAiReportDocumentsCallback(response, attachmentsData, requestId),
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getAiReportDocumentsCallbackError(response, attachmentsData, requestId),
			this.userIsBrand
		)
	}

	private getAiReportDocumentsCallback = (
		response: RestResponse<PaginatedResponse<Document>, ErrorResponse>,
		attachmentsData: PaginatedResponse<Document>,
		requestId: number
	) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.finalizeDocumentsLoading(attachmentsData, requestId)
			return
		}

		const mergedDocuments = this.mergeDocumentsData(attachmentsData, response.data)
		this.finalizeDocumentsLoading(mergedDocuments, requestId)
	}

	private getAiReportDocumentsCallbackError = (
		response: RestResponse<PaginatedResponse<Document>, ErrorResponse>,
		attachmentsData: PaginatedResponse<Document>,
		requestId: number
	) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.matesComponent.onException(response)
		this.finalizeDocumentsLoading(attachmentsData, requestId)
	}

	private finalizeDocumentsLoading = (documentsData: PaginatedResponse<Document>, requestId: number) => {
		this.setState(
			() => ({
				documentsData,
			}),
			() => this.getPhotos(requestId)
		)
	}

	private mergeDocumentsData = (
		baseDocuments: PaginatedResponse<Document>,
		extraDocuments?: PaginatedResponse<Document> | null
	): PaginatedResponse<Document> => {
		const extraItems = extraDocuments?.items ?? []

		if (extraItems.length === 0) {
			return baseDocuments
		}

		const basePagination = baseDocuments.pagination
		const updatedPagination = basePagination
			? {
				...basePagination,
				pageCount: basePagination.pageCount + extraItems.length,
				totalRecords: basePagination.totalRecords + extraItems.length,
			}
			: basePagination

		return {
			...baseDocuments,
			items: [...baseDocuments.items, ...extraItems],
			pagination: updatedPagination,
		}
	}

	private getDocumentsCallbackError = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.setState(() => ({ documentsData: null, loading: false }))
		this.matesComponent.onException(response)
	}

	private getPhotos = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		AssetService.getPhotos(
			this.assetId,
			DocumentType.PHOTO,
			this.state.photoFilter,
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getPhotosCallback(response, requestId),
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getPhotosCallbackError(response, requestId)
		)
	}

	private getPhotosCallback = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({
			photoData: response.data,
			loading: false,
		}))
	}

	private getPhotosCallbackError = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.setState(() => ({ photoData: null, loading: false }))
		this.matesComponent.onException(response)
	}

	private getLandlord = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		AssetService.getLandlord(
			(response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>) => this.getLandlordCallback(response, requestId),
			(response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>) => this.getLandlordCallbackError(response, requestId)
		)
	}

	private getLandlordCallback = (response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>, requestId: number): void => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({ landlordList: response.data }), () => this.getAsset(requestId))
	}

	private getLandlordCallbackError = (response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>, requestId: number): void => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.setState(() => ({ landlordList: null, loading: false }))
		this.matesComponent.onError(response)
	}

	private submitForm = (request: UpdateAssetRequest) => {
		this.setState(() => ({ loading: true, }))

		AssetService.updateAsset(
			this.assetId,
			request,
			this.updateAssetCallback,
			this.updateAssetCallbackError,
		)
	}

	private uploadDocument = (request: FormData) => {
		if (this.state.asset) {
			this.setState(() => ({
				loading: true
			}), () => AssetService.uploadDocument(this.state.asset!.asset.id, request, this.uploadDocumentCallback, this.matesComponent.onException))
		}
	}

	private uploadDocumentCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.getAsset()
	}

	private updateAssetCallback = (response: RestResponse<any, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		if (this.state.isSavingFloors) {
			this.setState(
				() => ({
					isSavingFloors: false,
					openFloorsModal: false,
				}),
				() => this.getAsset()
			)
			return
		}

		this.getAsset()
	}

	private updateAssetCallbackError = (response: RestResponse<any, ErrorResponse>): void => {
		this.setState(() => ({
			loading: false,
			isSavingFloors: false,
		}))
		this.matesComponent.onError(response)
	}

	private callbackDelete = (documentId: string) => {
		AssetService.deleteDocument(
			this.assetId,
			documentId,
			this.onDeleteCompleted,
			this.matesComponent.onException
		)
	}

	private openUploadDocumentModal = () => {
		this.setState(() => ({
			openUploadDocumentModal: true,
		}))
	}

	private closeUploadDocumentModal = (data?: FormData) => {
		if (data) {
			this.setState(() => ({
				openUploadDocumentModal: false,
			}), () => this.uploadDocument(data))
			return
		}

		this.setState(() => ({
			openUploadDocumentModal: false,
		}))
	}

	private openDeleteDocumentModal = (documentId: string) => {
		this.setState(() => ({
			openDeleteDocumentModal: true,
			selectedDocumentId: documentId,
		}))
	}

	private closeDeleteDocumentModal = () => {
		this.setState(() => ({
			openDeleteDocumentModal: false,
			selectedDocumentId: null,
		}))
	}

	private confirmDeleteDocument = () => {
		if (!this.state.selectedDocumentId) {
			return
		}

		const documentId = this.state.selectedDocumentId
		this.setState(() => ({
			openDeleteDocumentModal: false,
			selectedDocumentId: null,
		}), () => this.callbackDelete(documentId))
	}

	private onDeleteCompleted = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(
			() => ({
				loading: true,
				documentsFilter: {
					...this.state.documentsFilter,
					pageId: 0,
				},
			}),
			this.getDocuments
		)
	}

	private onPaginationChanged = (pageId: number, pageSize: number): void => {
		if (pageId !== this.state.documentsFilter.pageId || pageSize !== this.state.documentsFilter.pageSize) {
			this.setState(
				() => ({
					documentsFilter: {
						...this.state.documentsFilter,
						pageId: pageId,
						pageSize: pageSize,
					}
				}),
				this.getDocuments
			)
		}
	}
	private onSortChanged = (orderBy: string | null, orderType: OrderType | null): void => {
		if (orderBy !== this.state.documentsFilter.orderBy || orderType !== this.state.documentsFilter.orderType) {
			this.setState(
				() => ({
					documentsFilter: {
						...this.state.documentsFilter,
						orderBy: orderBy,
						orderType: orderType,
					}
				}),
				this.getDocuments
			)
		}
	}

	private deletePhoto = (photoId: string) => {
		AssetService.deleteDocument(this.assetId, photoId, this.deletePhotoCallback, this.matesComponent.onException)
	}

	private deletePhotoCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(
			() => ({
				loading: true,
				photoFilter: {
					...this.state.photoFilter,
					pageId: 0,
				},
			}),
			this.getPhotos
		)
	}

	private updateGmaps = () => {
		AssetService.updateGmaps(this.assetId, this.updateGmapsCallback, this.matesComponent.onException)
	}

	private updateGmapsCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.handleOpenNotification(
				t({ id: "asset-details.gmaps-update.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			return
		}

		this.handleOpenNotification(
			t({ id: "asset-details.gmaps-update.success", message: "Richiesta presa in carico. Aggiorna la pagina tra qualche istante." }),
			"success"
		)
	}

	private updateImmobiliare = () => {
		AssetService.updateImmobiliare(this.assetId, this.updateImmobiliareCallback, this.matesComponent.onException)
	}

	private updateImmobiliareCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.handleOpenNotification(
				t({ id: "asset-details.immobiliare-update.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			return
		}

		this.handleOpenNotification(
			t({ id: "asset-details.immobiliare-update.success", message: "Richiesta presa in carico. Prova a riaggiornare la pagina tra qualche istante." }),
			"success"
		)
	}

	private startAiAnalysis = () => {
		AssetService.startAiAnalysis(this.assetId, this.startAiAnalysisCallback, this.matesComponent.onException)
	}

	private startAiAnalysisCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.handleOpenNotification(
				t({ id: "asset-details.ai-analysis.start.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			return
		}

		this.handleOpenNotification(
			t({ id: "asset-details.ai-analysis.start.success", message: "Richiesta presa in carico. Aggiorna la pagina tra qualche istante." }),
			"success"
		)
	}

	private getAssetLikes = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		AccountServices.getAssetLikes(
			(response: RestResponse<any, ErrorResponse>) => this.getAssetLikesCallback(response, requestId),
			this.matesComponent.onException
		)
	}

	private getAssetLikesCallback = (response: RestResponse<any, ErrorResponse>, requestId: number): void => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		const likes = Array.isArray(response.data) ? response.data : []

		this.setState(() => ({
			likes: likes,
			likeAsset: likes.includes(this.assetId),
		}))
	}

	private toggleFavourite = () => {
		if (this.state.likeAsset) {
			this.removeFromFavourite()
			return
		}

		this.addToFavourite()
	}

	private addToFavourite = () => {
		AssetService.addToFavourite(this.assetId, this.addToFavouriteCallback, this.matesComponent.onException)
	}

	private addToFavouriteCallback = (response: RestResponse<any, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.getAssetLikes()
		this.handleOpenNotification(
			t({ id: "asset-details.favourite.added", message: "Immobile salvato tra i preferiti." }),
			"success"
		)
	}

	private removeFromFavourite = () => {
		AssetService.removeFromFavourite(this.assetId, this.removeFromFavouriteCallback, this.matesComponent.onException)
	}

	private removeFromFavouriteCallback = (response: RestResponse<any, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.getAssetLikes()
		this.handleOpenNotification(
			t({ id: "asset-details.favourite.removed", message: "Immobile rimosso dai preferiti." }),
			"info"
		)
	}

	private updateMatchSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState(() => ({
			matchSearch: event.target.value
		}))
	}

	private toggleMatchSelection = (matchId: string) => {
		this.setState((prevState) => {
			const nextSelected = new Set(prevState.selectedMatchIds)
			if (nextSelected.has(matchId)) {
				nextSelected.delete(matchId)
			} else {
				nextSelected.add(matchId)
			}

			return { selectedMatchIds: Array.from(nextSelected) }
		})
	}

	private toggleSelectAllMatches = (matchIds: string[]) => {
		this.setState((prevState) => {
			const allSelected = matchIds.every((matchId) => prevState.selectedMatchIds.includes(matchId))
			if (allSelected) {
				return { selectedMatchIds: prevState.selectedMatchIds.filter((matchId) => !matchIds.includes(matchId)) }
			}

			const nextSelected = new Set(prevState.selectedMatchIds)
			matchIds.forEach((matchId) => nextSelected.add(matchId))
			return { selectedMatchIds: Array.from(nextSelected) }
		})
	}

	private toggleMatchExpanded = (matchId: string) => {
		this.setState((prevState) => {
			const nextExpanded = new Set(prevState.expandedMatchIds)
			if (nextExpanded.has(matchId)) {
				nextExpanded.delete(matchId)
			} else {
				nextExpanded.add(matchId)
			}

			return { expandedMatchIds: Array.from(nextExpanded) }
		})
	}

	private openSingleMatchProposal = (match: AssetMatchBrand) => {
		const brand = match.brand
		if (!brand?.id) {
			return
		}

		const brandName = (brand.name ?? "").trim() || brand.company?.name || brand.id

		this.setState(() => ({
			openSendProposal: true,
			openPrecompiledProposal: true,
			selectedProposalBrands: [{ id: brand.id, name: brandName }],
		}))
	}

	private closeSendProposal = () => {
		this.setState(() => ({
			openSendProposal: false,
			openPrecompiledProposal: false,
			selectedProposalBrands: [],
		}))
	}

	private sendNewProposal = (request: SendNewProposalRequest) => {
		MatchService.sendNewProposal(request, this.sendNewProposalCallback, this.matesComponent.onException)
	}

	private sendNewProposalCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			this.handleOpenNotification(
				t({ id: "cnp.send-new.error", message: "Qualcosa è andato storto durante l'invio della proposta" }),
				"error"
			)
			this.closeSendProposal()
			return
		}

		this.handleOpenNotification(
			t({ id: "cnp.send-new.success", message: "Proposta inviata con successo" }),
			"success"
		)
		this.setState(() => ({
			openSendProposal: false,
			openPrecompiledProposal: false,
			selectedProposalBrands: [],
		}), this.getAsset)
	}

	private openLandlordModal = () => {
		if (!this.state.asset?.asset.landlord) {
			return
		}

		this.setState(() => ({
			openLandlordModal: true,
			landlordDetails: this.state.asset?.asset.landlord as LandlordDetails
		}))
	}

	private closeLandlordModal = () => {
		this.setState(() => ({
			openLandlordModal: false
		}))
	}

	private openLandlordCreationModal = () => {
		if (!this.state.asset) {
			return
		}

		this.setState(() => ({
			openLandlordCreationModal: true,
		}))
	}

	private closeLandlordCreationModal = () => {
		this.setState(() => ({
			openLandlordCreationModal: false,
		}))
	}

	private handleCreateLandlord = (request: CreateLandlordRequest) => {
		this.setState(() => ({ landlordCreationLoading: true }))
		LandlordService.createLandlord(
			request,
			this.createLandlordCallback,
			this.createLandlordCatchCallback
		)
	}

	private createLandlordCallback = (response: RestResponse<CreateLandlordResponse, ErrorResponse>) => {
		if (response.hasError()) {
			this.setState(() => ({ landlordCreationLoading: false }))
			this.matesComponent.onError(response)
			return
		}

		const landlordId = response.data?.id
		if (!landlordId) {
			this.setState(() => ({ landlordCreationLoading: false }))
			this.handleOpenNotification(
				t({ id: "landlord-create.error", message: "Creazione Landlord completata senza identificativo. Riprovare." }),
				"error"
			)
			return
		}

		this.associateLandlordWithAsset(landlordId)
	}

	private createLandlordCatchCallback = (response: RestResponse<CreateLandlordResponse, ErrorResponse>) => {
		this.setState(() => ({ landlordCreationLoading: false }))
		this.matesComponent.onException(response)
	}

	private associateLandlordWithAsset = (landlordId: string) => {
		LandlordService.associateLandlord(
			this.assetId,
			landlordId,
			this.associateLandlordCallback,
			this.associateLandlordErrorCallback
		)
	}

	private associateLandlordCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.setState(() => ({ landlordCreationLoading: false }))
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			openLandlordCreationModal: false,
			landlordCreationLoading: false,
			loading: true,
		}), () => {
			this.handleOpenNotification(
				t({ id: "landlord-create.success", message: "Landlord associato con successo all'immobile." }),
				"success"
			)
			this.getAsset()
		})
	}

	private associateLandlordErrorCallback = (response: RestResponse<any, ErrorResponse>) => {
		this.setState(() => ({ landlordCreationLoading: false }))
		this.matesComponent.onException(response)
	}

	private handleMetricDoubleClick = (metric: EditableAssetMetric) => {
		if (!this.state.asset) {
			return
		}

		this.setState((prevState) => ({
			editableMetrics: {
				...prevState.editableMetrics,
				[metric]: {
					...prevState.editableMetrics[metric],
					isEditing: true,
				},
			},
		}))
	}

	private handleMetricChange = (metric: EditableAssetMetric, value: string) => {
		this.setState((prevState) => ({
			editableMetrics: {
				...prevState.editableMetrics,
				[metric]: {
					...prevState.editableMetrics[metric],
					value,
				},
			},
		}))
	}

	private handleMetricKeyDown = (metric: EditableAssetMetric, event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault()
			this.submitEditableMetric(metric)
			return
		}

		if (event.key === "Escape") {
			event.preventDefault()
			this.resetEditableMetric(metric)
		}
	}

	private handleMetricBlur = (metric: EditableAssetMetric) => {
		const currentState = this.state.editableMetrics[metric]
		if (!currentState?.isEditing) {
			return
		}

		this.resetEditableMetric(metric)
	}

	private resetEditableMetric = (metric: EditableAssetMetric) => {
		const asset = this.state.asset?.asset ?? null
		const metricValue = this.getMetricNumericValue(asset, metric)
		this.setState((prevState) => ({
			editableMetrics: {
				...prevState.editableMetrics,
				[metric]: {
					value: metricValue !== null && metricValue !== undefined ? `${metricValue}` : "",
					isEditing: false,
				},
			},
		}))
	}

	private submitEditableMetric = (metric: EditableAssetMetric) => {
		const metricState = this.state.editableMetrics[metric]
		if (!metricState) {
			return
		}

		const parsedValue = this.parseMetricValue(metric, metricState.value)
		if (parsedValue === null) {
			return
		}

		const request = this.buildUpdateRequestForMetric(metric, parsedValue)
		if (!request) {
			return
		}

		this.setState((prevState) => ({
			editableMetrics: {
				...prevState.editableMetrics,
				[metric]: {
					value: metric === "bathrooms" ? (parsedValue > 0 ? "1" : "0") : `${parsedValue}`,
					isEditing: false,
				},
			},
		}), () => this.submitForm(request))
	}

	private parseMetricValue = (metric: EditableAssetMetric, rawValue: string): number | null => {
		const normalizedValue = rawValue.replace(",", ".").trim()
		if (!normalizedValue) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.invalid", message: "Valore non valido, inserire un numero." }),
				"error"
			)
			return null
		}

		const parsed = Number(normalizedValue)
		if (!Number.isFinite(parsed)) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.invalid", message: "Valore non valido, inserire un numero." }),
				"error"
			)
			return null
		}

		if (parsed < 0) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.negative", message: "Inserisci un valore maggiore o uguale a zero." }),
				"error"
			)
			return null
		}

		const requiresInteger = metric !== "height"
		if (requiresInteger && !Number.isInteger(parsed)) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.integer-only", message: "Inserisci un numero intero per questo campo." }),
				"error"
			)
			return null
		}

		return requiresInteger ? Math.trunc(parsed) : Number(parsed.toFixed(2))
	}

	private buildUpdateRequestForMetric = (metric: EditableAssetMetric, value: number): UpdateAssetRequest | null => {
		const overrides = this.getMetricOverrides(metric, value)
		if (!overrides) {
			return null
		}

		return this.buildUpdateRequest(overrides)
	}

	private getMetricOverrides = (metric: EditableAssetMetric, value: number): Partial<CreateCharacteristicsRequest> | null => {
		switch (metric) {
			case "gla":
				return { gla: value }
			case "height":
				return { height: value }
			case "shopWindows":
				return { shopWindows: value }
			case "bathrooms":
				return { toilet: value > 0 }
			default:
				return null
		}
	}

	private buildUpdateRequestForStatus = (status: AssetStatus): UpdateAssetRequest | null => {
		return this.buildUpdateRequest({ status })
	}

	private buildUpdateRequestWithAddress = (address: string): UpdateAssetRequest | null => {
		const request = this.buildUpdateRequest()
		if (!request) {
			return null
		}

		return {
			...request,
			address,
		}
	}

	private buildUpdateRequestWithDescription = (description: string): UpdateAssetRequest | null => {
		const request = this.buildUpdateRequest()
		if (!request) {
			return null
		}

		return {
			...request,
			description,
		}
	}

	private buildUpdateRequest = (overrides: Partial<CreateCharacteristicsRequest> = {}): UpdateAssetRequest | null => {
		const asset = this.state.asset?.asset ?? null
		if (!asset) {
			return null
		}

		const characteristics = asset.characteristics
		if (!characteristics) {
			return null
		}

		const updatedCharacteristics: CreateCharacteristicsRequest = {
			type: overrides.type ?? characteristics.type,
			status: overrides.status ?? characteristics.status,
			gla: overrides.gla ?? characteristics.gla ?? 0,
			position: overrides.position ?? characteristics.position,
			divisible: overrides.divisible ?? characteristics.divisible,
			shopWindows: overrides.shopWindows ?? characteristics.shopWindows ?? 0,
			contractType: overrides.contractType ?? characteristics.contractType,
			airConditioning: overrides.airConditioning ?? characteristics.airConditioning,
			fireProtection: overrides.fireProtection ?? characteristics.fireProtection,
			toilet: overrides.toilet ?? Boolean(characteristics.toilet),
			parkingSlots: overrides.parkingSlots ?? characteristics.parkingSlots ?? 0,
			floors: overrides.floors ?? characteristics.floors ?? [],
			height: overrides.height ?? characteristics.height ?? 0,
			price: overrides.price ?? characteristics.price ?? 0,
			fees: overrides.fees ?? characteristics.fees ?? 0,
			entryFee: overrides.entryFee ?? characteristics.entryFee ?? 0,
		}

		return {
			address: asset.address ?? "",
			landlordId: asset.landlord?.id ?? "",
			description: asset.description ?? "",
			links: asset.links ?? [],
			characteristics: updatedCharacteristics,
		}
	}

	private handleStatusDoubleClick = () => {
		if (!this.state.asset?.asset) {
			return
		}

		this.setState((prevState) => ({
			editableStatus: {
				...prevState.editableStatus,
				isEditing: true,
				value: (this.state.asset?.asset?.characteristics?.status ?? "") as AssetStatus | "",
			},
		}))
	}

	private handleStatusChange = (status: AssetStatus) => {
		this.setState((prevState) => ({
			editableStatus: {
				...prevState.editableStatus,
				value: status,
			},
		}))
	}

	private handleStatusKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault()
			this.submitStatusChange()
			return
		}

		if (event.key === "Escape") {
			event.preventDefault()
			this.resetEditableStatus()
		}
	}

	private handleStatusBlur = () => {
		if (!this.state.editableStatus.isEditing) {
			return
		}

		this.resetEditableStatus()
	}

	private resetEditableStatus = () => {
		this.setState(() => ({
			editableStatus: this.buildEditableStatusState(this.state.asset?.asset ?? null),
		}))
	}

	private submitStatusChange = () => {
		const asset = this.state.asset?.asset ?? null
		const selectedStatus = this.state.editableStatus.value
		if (!asset || !selectedStatus) {
			this.resetEditableStatus()
			return
		}

		const currentStatus = asset.characteristics?.status
		if (currentStatus === selectedStatus) {
			this.setState((prevState) => ({
				editableStatus: {
					...prevState.editableStatus,
					isEditing: false,
				},
			}))
			return
		}

		const request = this.buildUpdateRequestForStatus(selectedStatus)
		if (!request) {
			return
		}

		this.setState((prevState) => ({
			editableStatus: {
				...prevState.editableStatus,
				isEditing: false,
			},
		}), () => this.submitForm(request))
	}

	private handleAddressDoubleClick = () => {
		if (!this.state.asset?.asset) {
			return
		}

		this.setState(() => ({
			editableAddress: {
				value: this.state.asset?.asset?.address ?? "",
				isEditing: true,
			},
		}))
	}

	private handleAddressChange = (value: string) => {
		this.setState((prevState) => ({
			editableAddress: {
				...prevState.editableAddress,
				value,
			},
		}))
	}

	private handleAddressKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault()
			this.submitAddressChange()
			return
		}

		if (event.key === "Escape") {
			event.preventDefault()
			this.resetEditableAddress()
		}
	}

	private handleAddressBlur = () => {
		if (!this.state.editableAddress.isEditing) {
			return
		}

		this.resetEditableAddress()
	}

	private resetEditableAddress = () => {
		this.setState(() => ({
			editableAddress: this.buildEditableAddressState(this.state.asset?.asset ?? null),
		}))
	}

	private submitAddressChange = () => {
		const asset = this.state.asset?.asset ?? null
		if (!asset) {
			this.resetEditableAddress()
			return
		}

		const newAddress = this.state.editableAddress.value.trim()
		const currentAddress = asset.address ?? ""
		if (!newAddress) {
			this.handleOpenNotification(
				t({ id: "asset-details.address.invalid", message: "Inserisci un indirizzo valido." }),
				"error"
			)
			return
		}

		if (newAddress === currentAddress) {
			this.setState((prevState) => ({
				editableAddress: {
					...prevState.editableAddress,
					isEditing: false,
				},
			}))
			return
		}

		const request = this.buildUpdateRequestWithAddress(newAddress)
		if (!request) {
			return
		}

		this.setState((prevState) => ({
			editableAddress: {
				...prevState.editableAddress,
				isEditing: false,
				value: newAddress,
			},
		}), () => this.submitForm(request))
	}

	private handleDescriptionChange = (value: string) => {
		this.setState(() => ({
			editableDescription: {
				value,
			},
		}))
	}

	private handleDescriptionBlur = () => {
		this.submitDescriptionChange()
	}

	private resetEditableDescription = () => {
		this.setState(() => ({
			editableDescription: this.buildEditableDescriptionState(this.state.asset?.asset ?? null),
		}))
	}

	private submitDescriptionChange = () => {
		const asset = this.state.asset?.asset ?? null
		if (!asset) {
			this.resetEditableDescription()
			return
		}

		const newDescription = this.state.editableDescription.value ?? ""
		const currentDescription = asset.description ?? ""
		if (newDescription === currentDescription) {
			return
		}

		const request = this.buildUpdateRequestWithDescription(newDescription)
		if (!request) {
			return
		}

		this.submitForm(request)
	}

	private handlePriceDoubleClick = () => {
		if (!this.state.asset?.asset) {
			return
		}

		const currentPrice = this.state.asset.asset.characteristics?.price
		this.setState(() => ({
			editablePrice: {
				value: currentPrice !== null && currentPrice !== undefined ? `${currentPrice}` : "",
				isEditing: true,
			},
		}))
	}

	private handlePriceChange = (value: string) => {
		this.setState((prevState) => ({
			editablePrice: {
				...prevState.editablePrice,
				value,
			},
		}))
	}

	private handlePriceKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault()
			this.submitPriceChange()
			return
		}

		if (event.key === "Escape") {
			event.preventDefault()
			this.resetEditablePrice()
		}
	}

	private handlePriceBlur = () => {
		if (!this.state.editablePrice.isEditing) {
			return
		}

		this.resetEditablePrice()
	}

	private resetEditablePrice = () => {
		this.setState(() => ({
			editablePrice: this.buildEditablePriceState(this.state.asset?.asset ?? null),
		}))
	}

	private submitPriceChange = () => {
		const asset = this.state.asset?.asset ?? null
		if (!asset) {
			this.resetEditablePrice()
			return
		}

		const parsedPrice = this.parsePriceValue(this.state.editablePrice.value)
		if (parsedPrice === null) {
			return
		}

		const currentPrice = asset.characteristics?.price
		if (currentPrice !== null && currentPrice !== undefined) {
			const roundedCurrentPrice = Number(currentPrice.toFixed(2))
			if (roundedCurrentPrice === parsedPrice) {
				this.setState((prevState) => ({
					editablePrice: {
						...prevState.editablePrice,
						isEditing: false,
					},
				}))
				return
			}
		}

		const request = this.buildUpdateRequest({ price: parsedPrice })
		if (!request) {
			return
		}

		this.setState((prevState) => ({
			editablePrice: {
				...prevState.editablePrice,
				isEditing: false,
				value: `${parsedPrice}`,
			},
		}), () => this.submitForm(request))
	}

	private parsePriceValue = (rawValue: string): number | null => {
		const normalizedValue = rawValue.replace(",", ".").trim()
		if (!normalizedValue) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.invalid", message: "Valore non valido, inserire un numero." }),
				"error"
			)
			return null
		}

		const parsed = Number(normalizedValue)
		if (!Number.isFinite(parsed)) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.invalid", message: "Valore non valido, inserire un numero." }),
				"error"
			)
			return null
		}

		if (parsed < 0) {
			this.handleOpenNotification(
				t({ id: "asset-details.metric.negative", message: "Inserisci un valore maggiore o uguale a zero." }),
				"error"
			)
			return null
		}

		return Number(parsed.toFixed(2))
	}

	private handleAssetTypeDoubleClick = () => {
		if (!this.state.asset?.asset) {
			return
		}

		this.setState((prevState) => ({
			editableAssetType: {
				...prevState.editableAssetType,
				value: this.state.asset?.asset?.characteristics?.type ?? "",
				isEditing: true,
			},
		}))
	}

	private handleAssetTypeChange = (type: AssetType) => {
		this.setState((prevState) => ({
			editableAssetType: {
				...prevState.editableAssetType,
				value: type,
			},
		}))
	}

	private handleAssetTypeKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault()
			this.submitAssetTypeChange()
			return
		}

		if (event.key === "Escape") {
			event.preventDefault()
			this.resetEditableAssetType()
		}
	}

	private handleAssetTypeBlur = () => {
		if (!this.state.editableAssetType.isEditing) {
			return
		}

		this.resetEditableAssetType()
	}

	private resetEditableAssetType = () => {
		this.setState(() => ({
			editableAssetType: this.buildEditableAssetTypeState(this.state.asset?.asset ?? null),
		}))
	}

	private submitAssetTypeChange = () => {
		const asset = this.state.asset?.asset ?? null
		const newType = this.state.editableAssetType.value
		if (!asset || !newType) {
			this.resetEditableAssetType()
			return
		}

		if (!(Object.values(AssetType) as string[]).includes(newType)) {
			this.handleOpenNotification(
				t({ id: "asset-details.asset-type.invalid", message: "Seleziona una tipologia asset valida." }),
				"error"
			)
			return
		}

		const currentType = asset.characteristics?.type ?? ""
		if (currentType === newType) {
			this.setState((prevState) => ({
				editableAssetType: {
					...prevState.editableAssetType,
					isEditing: false,
				},
			}))
			return
		}

		const request = this.buildUpdateRequest({ type: newType })
		if (!request) {
			return
		}

		this.setState((prevState) => ({
			editableAssetType: {
				...prevState.editableAssetType,
				isEditing: false,
			},
		}), () => this.submitForm(request))
	}

	private handleOpenNotification = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity: severity
			}
		}))
	};

	private handleCloseNotification = () => {
		this.setState(() => ({
			snackbarData: {
				...this.state.snackbarData,
				openNotification: false
			}
		}))
	};

	private openQuickActionsPopover = () => {
		this.setState(() => ({ openQuickActions: true }))
	}

	private closeQuickActionsPopover = () => {
		this.setState(() => ({ openQuickActions: false }))
	}

	private openReminderModal = () => {
		this.setState(() => ({ openReminder: true, openQuickActions: false }))
	}

	private closeReminderModal = () => {
		this.setState(() => ({ openReminder: false }))
	}

	private openWorkflowModal = () => {
		this.setState(() => ({ openWorkflow: true, openQuickActions: false }))
	}

	private closeWorkflowModal = () => {
		this.setState(() => ({ openWorkflow: false }))
	}

	private openFloorsModal = () => {
		this.setState(() => ({ openFloorsModal: true, openQuickActions: false }))
	}

	private closeFloorsModal = () => {
		if (this.state.isSavingFloors) {
			return
		}

		this.setState(() => ({ openFloorsModal: false }))
	}

	private handleSaveFloors = (floors: Floor[]) => {
		const request = this.buildUpdateRequest({ floors })
		if (!request) {
			return
		}

		this.setState(() => ({ isSavingFloors: true }), () => this.submitForm(request))
	}

	private navigateToMatchProposal = () => {
		this.props.navigate(`/match-proposal/asset/${this.assetId}`)
		this.closeQuickActionsPopover()
	}

	private openImmobiliareLink = () => {
		const link = (this.state.immobiliareLink ?? "").trim()
		if (!link) {
			return
		}

		if (typeof window !== "undefined") {
			window.open(link, "_blank", "noopener,noreferrer")
		}

		this.closeQuickActionsPopover()
	}

	private createWorkflowFromTemplate = (templateId: string) => {
		const assetId = this.state.asset?.asset.id
		if (!assetId) {
			return
		}

		const request: CreateSpecificWorkflow = {
			objectId: assetId,
			objectType: WorkflowObjectType.ASSET,
			workflowTemplateId: templateId,
		}

		WorkflowService.createWorkflowFromSpecificObject(
			request,
			this.createWorkflowCallback,
			this.matesComponent.onError
		)
	}

	private createWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.handleOpenNotification(
			t({ id: "asset-form.create-workflow.success", message: "Workflow creato con successo." }),
			"success"
		)
		this.closeWorkflowModal()
	}

	private createReminder = (request: NewStepRequest) => {
		ActionsServices.createNewAction(request, this.createReminderCallback, this.matesComponent.onError)
	}

	private createReminderCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.handleOpenNotification(
				t({ id: "asset-form.create-reminder.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			this.closeReminderModal()
			return
		}

		this.handleOpenNotification(
			t({ id: "asset-form.create-reminder.success", message: "Promemoria creato con successo." }),
			"success"
		)
		this.closeReminderModal()
	}

	private formatCurrency = (value?: number | null, fractionDigits: number = 0): string => {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return "-"
		}

		return new Intl.NumberFormat("it-IT", {
			style: "currency",
			currency: "EUR",
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits,
		}).format(value)
	}

	private formatNumber = (value?: number | null, fractionDigits: number = 0): string => {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return "-"
		}

		return value.toLocaleString("it-IT", {
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits,
		})
	}

	private formatPercentage = (value?: number | null, signed: boolean = false): string => {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return "-"
		}

		const sign = signed && value > 0 ? "+" : ""
		return `${sign}${value}%`
	}

	private isPoiBrandRecognized = (poi: PoiEntry): boolean => {
		const value = poi.brand_riconosciuto
		if (typeof value === "string") {
			return value.toLowerCase() === "true"
		}

		return value === true
	}

	private getPoiName = (poi: PoiEntry, preferBrandName: boolean = false): string | null => {
		const candidates = preferBrandName
			? [poi.brand_nome, poi.nome, poi.name, poi.brand]
			: [poi.nome, poi.name, poi.brand_nome, poi.brand]

		for (const candidate of candidates) {
			if (typeof candidate === "string" && candidate.trim()) {
				return candidate.trim()
			}
		}

		return null
	}

	private getPoiAddress = (poi: PoiEntry): string | null => {
		const candidates = [poi.indirizzo, poi.address]
		for (const candidate of candidates) {
			if (typeof candidate === "string" && candidate.trim()) {
				return candidate.trim()
			}
		}
		return null
	}

	private formatDate = (value?: Date | string | null): string => {
		if (!value) {
			return "-"
		}

		const parsed = typeof value === "string" ? new Date(value) : value
		if (Number.isNaN(parsed.getTime())) {
			return "-"
		}

		return parsed.toLocaleDateString("it-IT")
	}

	private getProposalBrandOptions = (matches: AssetMatchBrand[]): BrandOption[] => {
		const options = new Map<string, BrandOption>()
		matches.forEach((match) => {
			const brand = match.brand
			if (!brand?.id) {
				return
			}

			const brandName = (brand.name ?? "").trim() || brand.company?.name || brand.id
			if (!options.has(brand.id)) {
				options.set(brand.id, { id: brand.id, name: brandName })
			}
		})

		return Array.from(options.values())
	}

	private parseAiAnalysisModel = (
		asset: Asset | null,
		assetDetails: Asset["assetDetails"] | null,
		descriptionText: string
	): AiAnalysisViewModel => {
		const aiAnalysisLegacy = (asset as (Asset & { ai_analysis?: unknown }) | null)?.ai_analysis
		const analysis = normalizeAiAnalysis(asset?.aiAnalysis ?? aiAnalysisLegacy ?? assetDetails?.aiAnalysis)
		const assetGla = asset?.characteristics?.gla ?? null
		const aiMinMqAnno = getAiAnalysisNumber(
			analysis,
			[
				"valutazione_mercato.quotazione.locazione.min_mq_anno",
				"valutazione_mercato.quotazione.min_mq_anno",
				"quotazione.locazione.min_mq_anno",
			],
			null
		)
		const aiMaxMqAnno = getAiAnalysisNumber(
			analysis,
			[
				"valutazione_mercato.quotazione.locazione.max_mq_anno",
				"valutazione_mercato.quotazione.max_mq_anno",
				"quotazione.locazione.max_mq_anno",
			],
			null
		)
		const aiPriceMin = aiMinMqAnno !== null && assetGla !== null && assetGla > 0
			? aiMinMqAnno * assetGla
			: assetDetails?.aiPriceMin ?? null
		const aiPriceMax = aiMaxMqAnno !== null && assetGla !== null && assetGla > 0
			? aiMaxMqAnno * assetGla
			: assetDetails?.aiPriceMax ?? null
		const averageMqPriceInArea = getAiAnalysisNumber(
			analysis,
			[
				"averageMqPriceInArea",
				"average_mq_price_in_area",
				"valutazione_mercato.quotazione.locazione.med_mq_anno",
				"valutazione_mercato.quotazione.med_mq_anno",
				"quotazione.locazionemed_mq_anno",
				"quotazione.locazione_med_mq_anno",
				"quotazione.locazioneMed_mq_anno",
				"quotazione.locazioneMedMqAnno",
			],
			assetDetails?.averageMqPriceInArea ?? null
		)
		const aiMinPerMq = aiMinMqAnno !== null
			? aiMinMqAnno
			: aiPriceMin !== null && assetGla !== null && assetGla > 0
				? aiPriceMin / assetGla
				: null
		const aiMaxPerMq = aiMaxMqAnno !== null
			? aiMaxMqAnno
			: aiPriceMax !== null && assetGla !== null && assetGla > 0
				? aiPriceMax / assetGla
				: null

		const poiList = getAiAnalysisValue(analysis, "poi.lista")
		const poiEntries: PoiEntry[] = Array.isArray(poiList) ? poiList as PoiEntry[] : []
		const poiMarkers = poiEntries.filter(
			(poi): poi is PoiEntry & { lat: number; lng: number } =>
				typeof poi.lat === "number" && typeof poi.lng === "number"
		)

		const descrizioneImmobile = getAiAnalysisValue(analysis, "descrizione_immobile") as Record<string, any> | null
		const puntiDiForzaValue = descrizioneImmobile?.punti_di_forza
		const puntiDiForza = Array.isArray(puntiDiForzaValue) ? puntiDiForzaValue : []
		const criticitaValue = descrizioneImmobile?.criticita
		const criticita = Array.isArray(criticitaValue) ? criticitaValue : []
		const valutazioneComplessiva = descrizioneImmobile?.valutazione_complessiva ?? null
		const targetIdealeValue = valutazioneComplessiva?.target_ideale
		const targetIdeale = Array.isArray(targetIdealeValue) ? targetIdealeValue : []
		const analysisBullets: string[] = []
		if (typeof descrizioneImmobile?.sintesi === "string" && descrizioneImmobile.sintesi.trim()) {
			analysisBullets.push(`Sintesi: ${descrizioneImmobile.sintesi.trim()}`)
		}
		puntiDiForza.forEach((item: Record<string, any>) => {
			const titolo = typeof item?.titolo === "string" ? item.titolo.trim() : ""
			const descrizione = typeof item?.descrizione === "string" ? item.descrizione.trim() : ""
			const categoria = typeof item?.categoria === "string" ? item.categoria.trim() : ""
			const detail = [titolo, descrizione].filter(Boolean).join(" - ")
			if (detail) {
				analysisBullets.push(`Punto di forza${categoria ? ` (${categoria})` : ""}: ${detail}`)
			}
		})
		criticita.forEach((item: Record<string, any>) => {
			const titolo = typeof item?.titolo === "string" ? item.titolo.trim() : ""
			const descrizione = typeof item?.descrizione === "string" ? item.descrizione.trim() : ""
			const mitigazione = typeof item?.mitigazione === "string" ? item.mitigazione.trim() : ""
			const detail = [titolo, descrizione].filter(Boolean).join(" - ")
			if (detail) {
				const mitigazioneLabel = mitigazione ? ` (Mitigazione: ${mitigazione})` : ""
				analysisBullets.push(`Criticita: ${detail}${mitigazioneLabel}`)
			}
		})
		const giudizio = typeof valutazioneComplessiva?.giudizio === "string" ? valutazioneComplessiva.giudizio.trim() : ""
		const aiOverallScore = typeof valutazioneComplessiva?.score === "number" ? valutazioneComplessiva.score : null
		if (giudizio || aiOverallScore !== null) {
			const scoreLabel = aiOverallScore !== null ? ` (score ${aiOverallScore})` : ""
			analysisBullets.push(`Valutazione complessiva: ${giudizio || "-"}${scoreLabel}`)
		}
		if (targetIdeale.length > 0) {
			analysisBullets.push(`Target ideale: ${targetIdeale.join(", ")}`)
		}
		if (analysisBullets.length === 0 && descriptionText) {
			analysisBullets.push(descriptionText)
		}

		const aiInsightValue = getAiAnalysisValue(analysis, "vocazione.descrizione")
		const aiInsightDescription = typeof aiInsightValue === "string" ? aiInsightValue.trim() : ""

		const averageIncome = getAiAnalysisNumber(
			analysis,
			[
				"averageIncome",
				"average_income",
				"dati_demografici.reddito_medio",
				"dati_demografici.reddito_medio_annuo",
				"dati_demografici.reddito_medio_pro_capite",
				"dati_demografici.reddito_medio_procapite",
			],
			assetDetails?.averageIncome ?? null
		)
		const averageAge = getAiAnalysisNumber(
			analysis,
			[
				"averageAge",
				"average_age",
				"dati_demografici.eta_media",
				"dati_demografici.eta_media_anni",
			],
			assetDetails?.averageAge ?? null
		)
		const educationPercentage = getAiAnalysisNumber(
			analysis,
			[
				"educationPercentage",
				"education_percentage",
				"highRankingEducationPercentage",
				"dati_demografici.percentuale_laureati",
				"dati_demografici.percentuale_istruzione_superiore",
				"dati_demografici.percentuale_diploma_superiore",
				"dati_demografici.percentuale_diploma",
			],
			assetDetails?.highRankingEducationPercentage ?? null
		)
		const educationLabelValue = getAiAnalysisValue(analysis, "dati_demografici.istruzione")
		const educationLabel = typeof educationLabelValue === "string" ? educationLabelValue.trim() : null

		return {
			analysis,
			analysisBullets,
			aiInsightDescription,
			poiEntries,
			poiMarkers,
			pricing: {
				aiMinMqAnno,
				aiMaxMqAnno,
				aiPriceMin,
				aiPriceMax,
				averageMqPriceInArea,
				aiMinPerMq,
				aiMaxPerMq,
			},
			averageIncome,
			averageAge,
			educationPercentage,
			educationLabel,
			overallScore: aiOverallScore,
		}
	}

	private getAssetTitle = (asset: Asset): string => {
		const address = asset.address ?? ""
		const city = asset.city ?? asset.zone?.city ?? ""

		if (address && city && !address.includes(city)) {
			return `${address} - ${city}`
		}

		return address || city || "-"
	}

	private getLandlordName = (landlord?: LandlordDetails | null): string => {
		const details = landlord?.account?.accountDetails
		const firstName = details?.firstName?.trim() ?? ""
		const lastName = details?.lastName?.trim() ?? ""
		const fullName = [firstName, lastName].filter(Boolean).join(" ")

		if (fullName) {
			return fullName
		}

		return landlord?.account?.username ?? "-"
	}

	private getLandlordLogo = (landlord?: LandlordDetails | null): string => {
		const logo = landlord?.logo?.trim()
		if (!logo) {
			return ""
		}

		const normalized = logo.toLowerCase()
		const isAbsoluteResource = normalized.startsWith("http://")
			|| normalized.startsWith("https://")
			|| normalized.startsWith("data:")
			|| normalized.startsWith("blob:")

		return isAbsoluteResource ? logo : `data:image/png;base64,${logo}`
	}

	private getCompatibilityLevel = (compatibility: number): "high" | "medium" | "low" => {
		const bounded = Math.max(0, Math.min(100, compatibility))
		if (bounded > 60) {
			return "high"
		}
		if (bounded > 33) {
			return "medium"
		}
		return "low"
	}

	private getCompatibilityColor = (level: "high" | "medium" | "low") => {
		switch (level) {
			case "high":
				return "#2e7d32"
			case "medium":
				return "#5aaac3"
			case "low":
			default:
				return "#c62828"
		}
	}

	private renderCompatibilityEmoticon = (compatibility: number) => {
		const level = this.getCompatibilityLevel(compatibility)
		const color = this.getCompatibilityColor(level)
		const iconPath = level === "high"
			? mdiEmoticonExcited
			: level === "medium"
				? mdiEmoticonNeutral
				: mdiEmoticonSad

		return (
			<Stack direction="row" spacing={0.5} alignItems="center">
				<Icon path={iconPath} size={1.2} color={color} />
			</Stack>
		)
	}

	private renderCompatibilityBar = (compatibility: number) => {
		const bounded = Math.max(0, Math.min(100, compatibility))
		const level = this.getCompatibilityLevel(bounded)
		const barColor = this.getCompatibilityColor(level)

		return (
			<Box sx={{ width: "100%", height: 10, borderRadius: 999, backgroundColor: "grey.200", overflow: "hidden" }}>
				<Box sx={{ width: `${bounded}%`, height: "100%", backgroundColor: barColor }} />
			</Box>
		)
	}

	render() {
		const userIsBrand = this.props.sessionData.accountType === AccountType.BRAND
		const asset = this.state.asset?.asset ?? null
		const assetDetails = asset?.assetDetails ?? null
		const matches = asset?.matches ?? []
		const filteredMatches = this.getFilteredMatches(matches, this.state.matchSearch)

		const sendableMatchIds = filteredMatches
			.filter((match) => match.status === AssetMatchesBrandStatus.CREATED)
			.map((match) => match.id)
		const allMatchesSelected = sendableMatchIds.length > 0 && sendableMatchIds.every((matchId) => this.state.selectedMatchIds.includes(matchId))
		const proposalBrandOptions = this.getProposalBrandOptions(
			matches.filter((match) => match.status !== AssetMatchesBrandStatus.SENT)
		)
		const documents = this.state.documentsData?.items ?? []

		const heroTitle = asset ? this.getAssetTitle(asset) : "-"
		const locationLine = [
			asset?.address,
			asset?.city,
			asset?.province ? `(${asset?.province})` : null,
		].filter(Boolean).join(", ")
		const zoneLabel = asset?.zone?.name ?? asset?.city ?? "-"
		const priceValue = asset?.characteristics?.price ?? null
		const priceLabel = this.formatCurrency(priceValue)
		const pricePerMq = priceValue && asset?.characteristics?.gla ? priceValue / asset.characteristics.gla : null
		const highlightChips: string[] = []
		const assetTypeValue = asset?.characteristics?.type ?? null
		const assetTypeLabel = assetTypeValue
			? t({ id: `enums.asset-types.${assetTypeValue}`, message: assetTypeValue })
			: "-"
		const featureChips: string[] = []
		const premiumLocation = asset?.characteristics?.position === PositionType.HIGH_STREET || asset?.characteristics?.position === PositionType.MALL
		if (premiumLocation) {
			highlightChips.push(t({ id: "asset-details.premium-location", message: "Premium Location" }))
		}
		if (asset?.characteristics?.airConditioning) {
			featureChips.push(t({ id: "asset-details.feature.air-conditioning", message: "Climatizzazione" }))
		}
		if (asset?.characteristics?.fireProtection) {
			featureChips.push(t({ id: "asset-details.feature.fire-protection", message: "Antincendio" }))
		}
		if (asset?.characteristics?.position) {
			featureChips.push(t({ id: `enums.asset-position.${asset.characteristics.position}`, message: asset.characteristics.position }))
		}
		if (asset?.characteristics?.contractType) {
			featureChips.push(t({ id: `enums.contract-type.${asset.characteristics.contractType}`, message: asset.characteristics.contractType }))
		}
		const statusOptions = (Object.values(AssetStatus) as AssetStatus[]).map((status) => ({
			value: status,
			label: t({ id: `enums.asset-status.${status}`, message: status }),
		}))

		const landlordName = this.getLandlordName(asset?.landlord ?? null)
		const landlordLogo = this.getLandlordLogo(asset?.landlord ?? null)
		const landlordNeedsAssociation = Boolean(asset?.landlord?.id === "-1")
		const descriptionText = assetDetails?.aiMarkdownResume || asset?.description || t({ id: "asset-details.description.missing", message: "Descrizione non disponibile." })
		const lastUpdateLabel = asset?.lastUpdate ? this.formatDate(asset.lastUpdate) : t({ id: "asset-details.last-update.today", message: "oggi" })
		const aiStartLabel = t({ id: "asset-details.ai-analysis.start", message: "Avvia analisi AI" })
		const bathroomsValue = this.getMetricNumericValue(asset, "bathrooms")
		const aiModel = this.parseAiAnalysisModel(asset, assetDetails, descriptionText)
		const assetCoordinates = asset?.location?.coordinates ?? null
		const assetLat = assetCoordinates && assetCoordinates.length >= 2 ? assetCoordinates[1] : null
		const assetLng = assetCoordinates && assetCoordinates.length >= 2 ? assetCoordinates[0] : null
		const poiMarkers = aiModel.poiMarkers
		const mapCenter = assetLat !== null && assetLng !== null
			? { lat: assetLat, lng: assetLng }
			: (poiMarkers.length > 0 ? { lat: poiMarkers[0].lat, lng: poiMarkers[0].lng } : { lat: 41.9028, lng: 12.4964 })
		const mapZoom = poiMarkers.length > 0 || (assetLat !== null && assetLng !== null) ? 15 : 5
		const googleMapsApiKey = "AIzaSyCJiclrDzpvGD3bqTSp3NHlB4wQ7j-b1XQ"
		const {
			analysisBullets,
			aiInsightDescription,
			poiEntries,
			averageIncome,
			averageAge,
			educationPercentage,
			educationLabel,
			pricing,
			overallScore,
		} = aiModel
		const {
			aiPriceMin,
			aiPriceMax,
			averageMqPriceInArea,
			aiMinPerMq,
			aiMaxPerMq,
		} = pricing
		const totalFootCountDaily = assetDetails?.totalFootCountDaily ?? null
		const pedestrianPassHours = assetDetails?.pedestrianPassHours ?? null
		const weekendTrafficIncrease = assetDetails?.weekendTrafficIncrease ?? null
		const hasTrafficInfo = totalFootCountDaily !== null || pedestrianPassHours || weekendTrafficIncrease !== null
		const trafficItems = [
			totalFootCountDaily !== null
				? { icon: <VisibilityOutlinedIcon fontSize="small" />, label: `${this.formatNumber(totalFootCountDaily)} passaggi/giorno` }
				: null,
			pedestrianPassHours
				? { icon: <AccessTimeIcon fontSize="small" />, label: `Zona pedonale ${pedestrianPassHours}` }
				: null,
			weekendTrafficIncrease !== null
				? { icon: <TrendingUpIcon fontSize="small" />, label: `${this.formatPercentage(weekendTrafficIncrease, true)} traffico weekend` }
				: null,
		].filter(Boolean) as { icon: React.ReactNode; label: string }[]
		const recognizedBrandPois = poiEntries.filter((poi) => this.isPoiBrandRecognized(poi))
		const unrecognizedPois = poiEntries.filter((poi) => !this.isPoiBrandRecognized(poi))
		const isBrandTabActive = this.state.neighborhoodTab === "brand"
		const activeNeighborhoodPois = isBrandTabActive ? recognizedBrandPois : unrecognizedPois
		const neighborhoodListMaxHeight = NEIGHBORHOOD_MAX_VISIBLE_ITEMS * 64
		const aiPriceRangeLabel = aiPriceMin !== null && aiPriceMax !== null
			? `${this.formatCurrency(aiPriceMin)} - ${this.formatCurrency(aiPriceMax)}`
			: "-"
		const averageMqPriceLabel = averageMqPriceInArea !== null
			? `${this.formatCurrency(averageMqPriceInArea, 2)} /mq`
			: "-"
		const pricePerMqLabel = pricePerMq !== null ? `${this.formatCurrency(pricePerMq, 2)} /mq` : "-"
		const priceDeltaLabel = pricePerMq !== null && averageMqPriceInArea !== null && averageMqPriceInArea !== 0
			? (() => {
				const delta = Math.round(((pricePerMq - averageMqPriceInArea) / averageMqPriceInArea) * 100)
				const sign = delta > 0 ? "+" : ""
				return `${sign}${delta}% vs media`
			})()
			: null
		const benchmarkValues = [
			{ label: t({ id: "asset-details.pricing.benchmark.asset", message: "Questo immobile" }), value: pricePerMq },
			{ label: t({ id: "asset-details.pricing.benchmark.average", message: "Media zona" }), value: averageMqPriceInArea },
			{ label: t({ id: "asset-details.pricing.benchmark.max", message: "Massimo zona" }), value: aiMaxPerMq },
			{ label: t({ id: "asset-details.pricing.benchmark.min", message: "Minimo zona" }), value: aiMinPerMq },
		].filter((entry) => entry.value !== null) as { label: string; value: number }[]
		const benchmarkMax = benchmarkValues.reduce((max, entry) => Math.max(max, entry.value), 0)

		return (
			<Box component="div" sx={{ pt: 2 }}>
				<Loading show={this.state.loading} />

				{userIsBrand ?
					<PageHeader
						title={RoutesData.ASSET_DETAILS.title}
						paths={[RoutesData.DASHBOARD, RoutesData.ASSET_DETAILS,]}
						params={{ "ASSET_DETAILS": { "id": this.assetId, } }}
					/> :
					<PageHeader
						title={RoutesData.ASSET_DETAILS.title}
						paths={[RoutesData.DASHBOARD, RoutesData.ASSET_LIST, RoutesData.ASSET_DETAILS,]}
						params={{ "ASSET_DETAILS": { "id": this.assetId, } }}
					/>
				}
				<Box component="div">
					{!this.state.loading && asset && (
						<Stack spacing={3}>
							<AssetHeroSection
								userIsBrand={userIsBrand}
								likeAsset={this.state.likeAsset}
								onToggleFavourite={this.toggleFavourite}
								immobiliareLink={this.state.immobiliareLink}
								aiStartLabel={aiStartLabel}
								onStartAiAnalysis={this.startAiAnalysis}
								heroTitle={heroTitle}
								asset={asset}
								sessionData={this.props.sessionData}
								photoData={this.state.photoData}
								onUploadPhoto={this.uploadDocument}
								onDeletePhoto={this.deletePhoto}
								highlightChips={highlightChips}
								assetTypeLabel={assetTypeLabel}
								zoneLabel={zoneLabel}
								locationLine={locationLine}
								priceLabel={priceLabel}
								pricePerMqLabel={pricePerMq !== null ? `${this.formatCurrency(pricePerMq, 2)} /mq` : "€ - /mq"}
								formatNumber={this.formatNumber}
								bathroomsValue={bathroomsValue}
								editableMetrics={this.state.editableMetrics}
								onMetricDoubleClick={this.handleMetricDoubleClick}
								onMetricChange={this.handleMetricChange}
								onMetricKeyDown={this.handleMetricKeyDown}
								onMetricBlur={this.handleMetricBlur}
								editableStatus={this.state.editableStatus}
								editableAddress={this.state.editableAddress}
								editablePrice={this.state.editablePrice}
								editableAssetType={this.state.editableAssetType}
								onStatusDoubleClick={this.handleStatusDoubleClick}
								onStatusChange={this.handleStatusChange}
								onStatusKeyDown={this.handleStatusKeyDown}
								onStatusBlur={this.handleStatusBlur}
								onAddressDoubleClick={this.handleAddressDoubleClick}
								onAddressChange={this.handleAddressChange}
								onAddressKeyDown={this.handleAddressKeyDown}
								onAddressBlur={this.handleAddressBlur}
								onPriceDoubleClick={this.handlePriceDoubleClick}
								onPriceChange={this.handlePriceChange}
								onPriceKeyDown={this.handlePriceKeyDown}
								onPriceBlur={this.handlePriceBlur}
								onAssetTypeDoubleClick={this.handleAssetTypeDoubleClick}
								onAssetTypeChange={this.handleAssetTypeChange}
								onAssetTypeKeyDown={this.handleAssetTypeKeyDown}
								onAssetTypeBlur={this.handleAssetTypeBlur}
								statusOptions={statusOptions}
								featureChips={featureChips}
								landlordLogo={landlordLogo}
								landlordName={landlordName}
								onOpenLandlordModal={this.openLandlordModal}
								canAssociateLandlord={landlordNeedsAssociation}
								onAssociateLandlord={this.openLandlordCreationModal}
								isQuickActionsOpen={this.state.openQuickActions}
								onOpenQuickActions={this.openQuickActionsPopover}
								onCloseQuickActions={this.closeQuickActionsPopover}
								onQuickActionSendProposal={this.navigateToMatchProposal}
								onQuickActionOpenImmobiliare={this.openImmobiliareLink}
								onQuickActionManageFloors={this.openFloorsModal}
								onQuickActionCreateReminder={this.openReminderModal}
								onQuickActionTriggerWorkflow={this.openWorkflowModal}
							/>
							<Box sx={{ borderRadius: 2, border: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
								<Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
									<Typography fontWeight={600}>
										<Trans id="asset-details.description.title">Descrizione dell'immobile</Trans>
									</Typography>
									<Typography variant="caption" color="text.secondary">
										<Trans id="asset-details.description.subtitle">Personalizza il testo mostrato ai brand</Trans>
									</Typography>
								</Box>
								<Box sx={{ px: 3, py: 2 }}>
									<TextField
										fullWidth
										multiline
										minRows={4}
										maxRows={12}
										value={this.state.editableDescription.value}
										onChange={(event) => this.handleDescriptionChange(event.target.value)}
										onBlur={this.handleDescriptionBlur}
										placeholder={t({ id: "asset-details.description.placeholder", message: "Aggiungi una descrizione dettagliata dell'immobile" })}
										disabled={this.state.loading || !asset}
									/>
								</Box>
							</Box>
							<Box sx={{ borderRadius: 2, border: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={2}
									alignItems={{ xs: "flex-start", sm: "center" }}
									justifyContent="space-between"
									sx={{ px: 2, py: 2, borderBottom: 1, borderColor: "divider" }}
								>
									<Stack direction="row" spacing={2} alignItems="center">
										<Box
											sx={{
												width: 44,
												height: 44,
												borderRadius: "50%",
												backgroundColor: "primary.main",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												color: "primary.contrastText",
											}}
										>
											<AutoAwesomeIcon />
										</Box>
										<Box>
											<Typography fontWeight={600}>
												<Trans id="asset-details.ai-analysis.title">Analisi AI dell'immobile</Trans>
											</Typography>
											<Typography variant="caption" color="text.secondary">
												<Trans id="asset-details.ai-analysis.subtitle">
													Generata automaticamente con AI proprietaria
												</Trans>
											</Typography>
										</Box>
									</Stack>
									<Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
										<Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
											<Trans id="asset-details.ai-analysis.score-label">Valutazione complessiva</Trans>
										</Typography>
										<Typography variant="h6" fontWeight={700}>
											{overallScore !== null ? this.formatNumber(overallScore, Number.isInteger(overallScore) ? 0 : 1) : "-"}
										</Typography>
									</Box>
								</Stack>
								<Box sx={{ px: 2, py: 2 }}>
									<Box sx={{ borderRadius: 2, border: 1, borderColor: "primary.main", px: 2, py: 2 }}>
										<Typography variant="subtitle2" color="text.secondary">
											<Trans id="asset-details.ai-analysis.description">Descrizione</Trans>
										</Typography>
										<Box sx={{ mt: 1 }}>
											{analysisBullets.length > 0 ? (
												<Box component="ul" sx={{ p: 0, m: 0, listStyle: "none" }}>
													{analysisBullets.map((item, index) => (
														<Box
															component="li"
															key={`ai-desc-${index}`}
															sx={{
																display: "flex",
																alignItems: "flex-start",
																gap: 1.25,
																mb: 1.25,
																listStyle: "none",
															}}
														>
															<Box
																sx={{
																	width: index === 0 ? 34 : 24,
																	height: index === 0 ? 34 : 24,
																	borderRadius: "50%",
																	background: index === 0
																		? "linear-gradient(135deg, #00a3e5, #2daeb0)"
																		: "rgba(0, 163, 229, 0.12)",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	color: index === 0 ? "common.white" : "#00a3e5",
																	flexShrink: 0,
																}}
															>
																{index === 0 ? (
																	<AutoAwesomeIcon sx={{ fontSize: 18 }} />
																) : (
																	<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "currentColor" }} />
																)}
															</Box>
															<Box sx={{ flex: 1 }}>
																{index === 0 && (
																	<Typography
																		variant="caption"
																		color="text.secondary"
																		sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
																	>
																		<Trans id="asset-details.ai-analysis.summary.label">Sintesi</Trans>
																	</Typography>
																)}
																<Typography
																	variant={index === 0 ? "body1" : "body2"}
																	sx={{ lineHeight: 1.7, fontWeight: index === 0 ? 600 : 500 }}
																>
																	{item}
																</Typography>
															</Box>
														</Box>
													))}
												</Box>
											) : (
												<Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
													{descriptionText}
												</Typography>
											)}
										</Box>
										{hasTrafficInfo && trafficItems.length > 0 && (
											<>
												<Divider sx={{ my: 2 }} />
												<Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
													{trafficItems.map((item, index) => (
														<Stack key={`traffic-${index}`} direction="row" spacing={1} alignItems="center">
															<Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
																{item.icon}
															</Box>
															<Typography variant="caption" color="text.secondary">
																{item.label}
															</Typography>
														</Stack>
													))}
												</Stack>
											</>
										)}
									</Box>
								</Box>
							</Box>
							{(aiPriceMin !== null || aiPriceMax !== null || averageMqPriceInArea !== null || priceValue !== null) && (
								<Box sx={{ borderRadius: 2, border: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
									<Box sx={{ px: 3, py: 2 }}>
										<Typography fontWeight={600}>
											<Trans id="asset-details.pricing.title">Valutazione dell'immobile</Trans>
										</Typography>
										<Typography variant="caption" color="text.secondary">
											<Trans id="asset-details.pricing.subtitle">Analisi comparativa AI-powered</Trans>
										</Typography>
									</Box>
									<Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
										<Grid item xs={12} md={3}>
											<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#d6f0d6" }}>
												<Typography variant="caption" color="text.secondary">
													<Trans id="asset-details.pricing.ai-estimate">Valore stimato AI</Trans>
												</Typography>
												<Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
													{aiPriceRangeLabel}
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12} md={3}>
											<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default" }}>
												<Typography variant="caption" color="text.secondary">
													<Trans id="asset-details.pricing.requested">Prezzo richiesto</Trans>
												</Typography>
												<Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
													{priceLabel}
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12} md={3}>
											<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default" }}>
												<Typography variant="caption" color="text.secondary">
													<Trans id="asset-details.pricing.area-average">Media zona</Trans>
												</Typography>
												<Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
													{averageMqPriceLabel}
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12} md={3}>
											<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default" }}>
												<Typography variant="caption" color="text.secondary">
													<Trans id="asset-details.pricing.this-asset">Questo immobile</Trans>
												</Typography>
												<Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
													{pricePerMqLabel}
												</Typography>
												{priceDeltaLabel && (
													<Chip
														label={priceDeltaLabel}
														size="small"
														sx={{ mt: 1, backgroundColor: "#d4f0e4", color: "#3b8b6a" }}
													/>
												)}
											</Box>
										</Grid>
									</Grid>
									{benchmarkValues.length > 0 && (
										<Box sx={{ px: 2, pb: 3 }}>
											<Typography fontWeight={600} sx={{ mb: 2 }}>
												<Trans id="asset-details.pricing.benchmark.title">
													Benchmark Prezzi - {zoneLabel}
												</Trans>
											</Typography>
											<Stack spacing={2}>
												{benchmarkValues.map((entry) => {
													const width = benchmarkMax > 0 ? Math.round((entry.value / benchmarkMax) * 100) : 0
													return (
														<Box key={entry.label}>
															<Stack direction="row" alignItems="center" justifyContent="space-between">
																<Typography variant="caption" color="text.secondary">
																	{entry.label}
																</Typography>
																<Typography variant="caption" color="text.secondary">
																	{entry.value !== null ? `${this.formatCurrency(entry.value, 2)} /mq` : "-"}
																</Typography>
															</Stack>
															<Box sx={{ mt: 0.5, height: 10, borderRadius: 999, backgroundColor: "grey.200", overflow: "hidden" }}>
																<Box sx={{ width: `${width}%`, height: "100%", backgroundColor: "#5e9db7" }} />
															</Box>
														</Box>
													)
												})}
											</Stack>
										</Box>
									)}
								</Box>
							)}
							<Box sx={{ borderRadius: 2, border: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
								<Stack
									direction="row"
									spacing={2}
									alignItems="center"
									sx={{ px: 2, py: 2, borderBottom: 1, borderColor: "divider" }}
								>
									<Box
										sx={{
											width: 44,
											height: 44,
											borderRadius: "50%",
											backgroundColor: "primary.main",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "primary.contrastText",
										}}
									>
										<MapOutlinedIcon />
									</Box>
									<Box>
										<Typography fontWeight={600}>
											<Trans id="asset-details.neighborhood.title">Analisi del quartiere</Trans>
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{zoneLabel}
										</Typography>
									</Box>
								</Stack>
								<Grid container spacing={2} sx={{ px: 2, py: 2 }}>
									<Grid item xs={12} md={8}>
										<Box
											sx={{
												borderRadius: 2,
												minHeight: 240,
												height: 320,
												overflow: "hidden",
												border: 1,
												borderColor: "divider",
											}}
										>
											<LoadScript googleMapsApiKey={googleMapsApiKey}>
												<GoogleMap
													id="asset-neighborhood-map"
													mapContainerStyle={{ height: "100%", width: "100%" }}
													center={mapCenter}
													zoom={mapZoom}
													options={{
														streetViewControl: false,
														mapTypeControl: false,
														fullscreenControl: false,
													}}
													onClick={() => this.setState({ selectedPoiIndex: null })}
												>
													{assetLat !== null && assetLng !== null && (
														<Marker
															position={{
																lat: assetLat,
																lng: assetLng,
															}}
															icon="//maps.google.com/mapfiles/ms/icons/blue-dot.png"
														/>
													)}
													{poiMarkers.map((poi, index) => (
														<Marker
															key={poi.place_id || `${poi.name ?? "poi"}-${index}`}
															position={{
																lat: poi.lat,
																lng: poi.lng,
															}}
															title={this.getPoiName(poi) ?? undefined}
															onClick={() => this.setState({ selectedPoiIndex: index })}
														/>
													))}
													{this.state.selectedPoiIndex !== null && poiMarkers[this.state.selectedPoiIndex] && (
														<InfoWindow
															position={{
																lat: poiMarkers[this.state.selectedPoiIndex].lat,
																lng: poiMarkers[this.state.selectedPoiIndex].lng,
															}}
															onCloseClick={() => this.setState({ selectedPoiIndex: null })}
														>
															<Typography variant="body2" fontWeight={600}>
																{this.getPoiName(poiMarkers[this.state.selectedPoiIndex]) ?? "-"}
															</Typography>
														</InfoWindow>
													)}
												</GoogleMap>
											</LoadScript>
										</Box>
										<Box sx={{ mt: 3 }}>
											<Typography fontWeight={600}>
												<Trans id="asset-details.neighborhood.profile">Profilo Socio-Economico dell'Area</Trans>
											</Typography>
											<Grid container spacing={2} sx={{ mt: 0.5 }}>
												<Grid item xs={12} md={4}>
													<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default", border: 1, borderColor: "divider" }}>
														<Typography variant="caption" color="text.secondary">
															<Trans id="asset-details.neighborhood.income">Reddito medio</Trans>
														</Typography>
														<Typography fontWeight={600} variant="h6">
															{this.formatCurrency(averageIncome)}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															<Trans id="asset-details.neighborhood.income.label">anno pro-capite</Trans>
														</Typography>
													</Box>
												</Grid>
												<Grid item xs={12} md={4}>
													<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default", border: 1, borderColor: "divider" }}>
														<Typography variant="caption" color="text.secondary">
															<Trans id="asset-details.neighborhood.age">Età media</Trans>
														</Typography>
														<Typography fontWeight={600} variant="h6">
															{this.formatNumber(averageAge)}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															<Trans id="asset-details.neighborhood.age.label">popolazione residente</Trans>
														</Typography>
													</Box>
												</Grid>
												<Grid item xs={12} md={4}>
													<Box sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default", border: 1, borderColor: "divider" }}>
														<Typography variant="caption" color="text.secondary">
															<Trans id="asset-details.neighborhood.education">Istruzione</Trans>
														</Typography>
														<Typography fontWeight={600} variant="h6">
															{educationLabel ?? this.formatPercentage(educationPercentage)}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															<Trans id="asset-details.neighborhood.education.second-label">livello medio</Trans>
														</Typography>
													</Box>
												</Grid>
											</Grid>
											<Box sx={{ mt: 2, p: 2, borderRadius: 2, border: 1, borderColor: "secondary.main" }}>
												<Stack direction="row" spacing={2} alignItems="flex-start">
													<AutoAwesomeIcon color="secondary" />
													<Box>
														<Typography fontWeight={600} color="secondary.main">
															<Trans id="asset-details.neighborhood.insight.title">Insight AI</Trans>
														</Typography>
														<Typography variant="body2" color="text.secondary">
															{aiInsightDescription || (
																<Trans id="asset-details.neighborhood.insight.placeholder">
																	insight non disponibile
																</Trans>
															)}
														</Typography>
													</Box>
												</Stack>
											</Box>
										</Box>
									</Grid>
									<Grid item xs={12} md={4}>
										<Box sx={{ pl: { md: 2 }, borderLeft: { md: "1px solid" }, borderColor: { md: "divider" }, height: "100%" }}>
											<Stack spacing={2}>
												<Box>
													<Typography fontWeight={600}>
														<Trans id="asset-details.neighborhood.brands">Brand nell'area</Trans>
													</Typography>
													<Typography variant="caption" color="text.secondary">
														<Trans id="asset-details.neighborhood.radius">Raggio 250m</Trans>
													</Typography>
												</Box>
												<Tabs
													value={this.state.neighborhoodTab}
													onChange={this.handleNeighborhoodTabChange}
													variant="fullWidth"
													textColor="primary"
													indicatorColor="primary"
													aria-label="Selezione Brand o POI"
													sx={{
														minHeight: 36,
														"& .MuiTab-root": { minHeight: 36, fontSize: 14, fontWeight: 600 },
													}}
												>
													<Tab value="brand" label={<Trans id="asset-details.neighborhood.tab.brand">Brand</Trans>} />
													<Tab value="poi" label={<Trans id="asset-details.neighborhood.tab.poi">POI</Trans>} />
												</Tabs>
												<Stack
													spacing={1}
													sx={{
														maxHeight: neighborhoodListMaxHeight,
														overflowY: "auto",
														pr: 0.5,
													}}
												>
													{activeNeighborhoodPois.length > 0 ? (
														activeNeighborhoodPois.map((poi, index) => {
															const primaryLabel = (isBrandTabActive ? this.getPoiName(poi, true) : this.getPoiName(poi)) ?? "-"
															const addressLabel = this.getPoiAddress(poi) ?? "-"
															const avatarLabel = primaryLabel !== "-" ? primaryLabel.charAt(0).toUpperCase() : "?"
															return (
																<Stack
																	key={poi.place_id || `${primaryLabel}-${index}`}
																	direction="row"
																	spacing={1}
																	alignItems="center"
																	sx={{
																		p: 1,
																		borderRadius: 1,
																		backgroundColor: "background.default",
																		border: 1,
																		borderColor: "divider",
																	}}
																>
																	<Avatar sx={{ width: 36, height: 36 }}>
																		{avatarLabel}
																	</Avatar>
																	<Box sx={{ flex: 1 }}>
																		<Typography fontWeight={600}>
																			{primaryLabel}
																		</Typography>
																		<Typography variant="caption" color="text.secondary">
																			{addressLabel}
																		</Typography>
																	</Box>
																</Stack>
															)
														})
													) : (
														<Typography variant="body2" color="text.secondary">
															{isBrandTabActive ? (
																<Trans id="asset-details.neighborhood.brands.empty">Nessun brand disponibile.</Trans>
															) : (
																<Trans id="asset-details.neighborhood.poi.empty">Nessun punto di interesse disponibile.</Trans>
															)}
														</Typography>
													)}
												</Stack>
											</Stack>
										</Box>
									</Grid>
								</Grid>
							</Box>
							<AssetBrandMatchingSection
								filteredMatches={filteredMatches}
								sendableMatchIds={sendableMatchIds}
								allMatchesSelected={allMatchesSelected}
								matchSearch={this.state.matchSearch}
								selectedMatchIds={this.state.selectedMatchIds}
								expandedMatchIds={this.state.expandedMatchIds}
								onToggleSelectAll={this.toggleSelectAllMatches}
								onSearchChange={this.updateMatchSearch}
								onToggleMatchSelection={this.toggleMatchSelection}
								onToggleMatchExpanded={this.toggleMatchExpanded}
								onOpenSingleMatchProposal={this.openSingleMatchProposal}
								onNavigateToBrand={(brandId) => this.props.navigate(`/brand/${brandId}`)}
								renderCompatibilityEmoticon={this.renderCompatibilityEmoticon}
								renderCompatibilityBar={this.renderCompatibilityBar}
							/>
							<AssetDocumentsSection
								documents={documents}
								userIsBrand={userIsBrand}
								assetId={this.assetId}
								onOpenUploadModal={this.openUploadDocumentModal}
								onOpenDeleteModal={this.openDeleteDocumentModal}
								formatDate={this.formatDate}
							/>
						</Stack>
					)}
					{!this.state.loading && !asset && (
						<Typography sx={{ p: 2 }} color="text.secondary">
							<Trans id="asset-details.not-found">Immobile non disponibile.</Trans>
						</Typography>
					)}
					<SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />
					{this.state.openFloorsModal && asset && (
						<ManageFloorsModal
							open={this.state.openFloorsModal}
							floors={asset.characteristics?.floors ?? []}
							onClose={this.closeFloorsModal}
							onSave={this.handleSaveFloors}
							isSaving={this.state.isSavingFloors}
						/>
					)}
					{this.state.openLandlordModal && this.state.landlordDetails && (
						<LandlordModal closeLandlordModal={this.closeLandlordModal} landlord={this.state.landlordDetails} />
					)}
					{this.state.openLandlordCreationModal && (
						<LandlordCreateModal
							open={this.state.openLandlordCreationModal}
							onClose={this.closeLandlordCreationModal}
							onSubmit={this.handleCreateLandlord}
							isSubmitting={this.state.landlordCreationLoading}
							onApiError={this.matesComponent.onError}
						/>
					)}
					{this.state.openUploadDocumentModal && (
						<UploadDocumentModal
							open={this.state.openUploadDocumentModal}
							onClose={this.closeUploadDocumentModal}
							callbackUploadDocument={this.closeUploadDocumentModal}
						/>
					)}
					{this.state.openDeleteDocumentModal && (
						<DeleteDocumentModal
							onClose={this.closeDeleteDocumentModal}
							onDelete={this.confirmDeleteDocument}
						/>
					)}
					{this.state.openSendProposal && asset && (
						<SendNewProposal
							key={this.state.selectedProposalBrands.map((brand) => brand.id).join("-") || "send-proposal"}
							asset={asset}
							onClose={this.closeSendProposal}
							submit={this.sendNewProposal}
							selectedBrands={this.state.selectedProposalBrands}
							allBrands={proposalBrandOptions}
							precompiled={this.state.openPrecompiledProposal}
						/>
					)}
					{this.state.openWorkflow && (
						<TemplateSelectionModal
							templateType={WorkflowObjectType.ASSET}
							onClose={this.closeWorkflowModal}
							submit={this.createWorkflowFromTemplate}
						/>
					)}
					{this.state.openReminder && (
						<NewStep
							openDialog={this.state.openReminder}
							onClose={this.closeReminderModal}
							submit={this.createReminder}
							action
							create
							workflowType={WorkflowObjectType.ASSET}
						/>
					)}
				</Box>
			</Box>
		)
	}
}

export default withUseRouter(AssetDetails)
